import React, { useState } from 'react'
import { observer } from 'mobx-react-lite'
import clsx from 'clsx'

import { makeStyles } from '@material-ui/core/styles'
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TableCell,
  TextField,
  Typography
} from '@material-ui/core'

import { useLogic } from '../../../hooks/storeHook'
import { useBoolState } from '../../../hooks/commonHooks'

import { emDash } from '../../../common/stringUtility'

import { MyRowProps } from '../../../types/tableTypes'
import { UserModel } from '../../../server/mpsklad_core/Models/UserModel'

export const AdminCommentCell =
  observer(({row}: MyRowProps<UserModel>) => {
    const currentComment = row.adminComment || null

    const {setUserAdminComment} = useLogic()
    const classes = useStyles()

    const [newComment, setNewComment] = useState(currentComment)
    const [isModalOpen, setModalOpen, setModalClosed] = useBoolState()
    const [isSubmitting, setSubmitting, setSubmitted] = useBoolState()

    const onCancelEdit = () => {
      setNewComment(currentComment)
      setModalClosed()
    }

    const onCloseModal = () => {
      if (!isSubmitting) {
        setModalClosed()
      }
    }

    const onSaveComment = async () => {
      if (isSubmitting) {
        return
      }

      setSubmitting()

      try {
        await setUserAdminComment(row.id, newComment)
        setModalClosed()
      } catch (e) {
        console.error('Failed to save admin comment', e)
      } finally {
        setSubmitted()
      }
    }

    // NOTE: Nested dialog can't coexist with cell click events
    return (
      <TableCell className={clsx('table-border', classes.cell)} onClick={!isModalOpen ? setModalOpen : undefined}>
        {/* Box is slow for many rows (in dev, at least) */}
        <span title={currentComment ?? 'Добавить комментарий'}>
          <Typography variant="body2" noWrap>
            {currentComment ?? emDash}
          </Typography>
        </span>

        <Dialog
          open={isModalOpen}
          onClose={onCloseModal}
          aria-labelledby="edit-comment-dialog-title"
          fullWidth
          maxWidth="sm"
          container={document.body}
        >
          <DialogTitle id="edit-comment-dialog-title">Редактировать комментарий</DialogTitle>

          <DialogContent>
            <TextField
              autoFocus
              fullWidth
              multiline
              id="admin-comment"
              label="Комментарий администратора"
              margin="dense"
              type="text"
              minRows="4"
              variant="outlined"
              value={newComment}
              disabled={isSubmitting}
              onChange={e => setNewComment(e.target.value)}
            />
          </DialogContent>

          <DialogActions>
            <Button color="secondary" disabled={isSubmitting} onClick={onCancelEdit}>
              Отмена
            </Button>

            <Button
              onClick={onSaveComment}
              color="primary"
              disabled={isSubmitting}
              variant="contained"
            >
              {isSubmitting ? <CircularProgress size={24} color="inherit"/> : 'Сохранить'}
            </Button>
          </DialogActions>
        </Dialog>
      </TableCell>
    )
  })

const useStyles = makeStyles(({palette}) => ({
  cell: {
    maxWidth: '200px !important',
    border: 'none',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: palette.action.hover
    }
  }
}))